/**
 * Product Accordion Component (Parent Level)
 *
 * @package
 */

import { useState, useEffect, useRef, useId } from '@wordpress/element';
import { _n, sprintf } from '@wordpress/i18n';
import ProductCard from './ProductCard';
import NestedProductAccordion from './NestedProductAccordion';
import { useGroupVariants } from '../helpers/useGroupVariants';
import { getActiveVariantId, isProductSelected, makeGroupSelectionHandlers } from '../helpers/variantSelection';
import { countGroupOptions, subGroupHasOptions } from '../helpers/groupOptions';

const ProductAccordion = ( { group, selectedProducts, setSelectedProducts, lockedProducts = [], vatMode = 'including', quantities = {}, setProductQuantity, workshopHourlyRate = 0, taxRate = 0 } ) => {
	const startClosed = group.group_accordion_start_closed === true || group.group_accordion_start_closed === 1;
	const [ isOpen, setIsOpen ] = useState( ! startClosed );
	const wrapperRef = useRef( null );
	const headerRef = useRef( null );

	// Expose the header height so sticky nested accordion headers can stack below it.
	useEffect( () => {
		const wrapper = wrapperRef.current;
		const header = headerRef.current;
		if ( ! wrapper || ! header ) {
			return;
		}

		const setHeightProperty = () => {
			wrapper.style.setProperty( '--at-group-header-height', `${ header.offsetHeight }px` );
		};

		setHeightProperty();
		const observer = new window.ResizeObserver( setHeightProperty );
		observer.observe( header );

		return () => observer.disconnect();
	}, [] );

	const selectionType = group.group_selection_type || 'checkbox';
	const cardLayout = group.group_card_layout || 'horizontal';

	const groupProductIds = group.products || [];
	const hasLockedProductInGroup = lockedProducts.some( lockedId => groupProductIds.includes( lockedId ) );
	const isRadioGroupDisabled = selectionType === 'radio' && hasLockedProductInGroup;

	const { variantsMap, groupVariantIds } = useGroupVariants( groupProductIds );
	const { toggleProduct, selectVariant } = makeGroupSelectionHandlers( {
		selectionType,
		groupProductIds,
		groupVariantIds,
		selectedProducts,
		setSelectedProducts,
	} );

	const accordionId = `accordion${ useId() }`;
	const contentId = `${ accordionId }-content`;

	const groupName = group.group_name || 'Product Group';

	const totalOptions = countGroupOptions( group );

	// An empty sub-group would render as a header with nothing under it.
	const populatedSubGroups = ( group.sub_groups || [] ).filter( subGroupHasOptions );

	// Sub-group variants are needed here too: selecting a variant stores the variant ID, not the parent's.
	const subGroupProductIds = populatedSubGroups.flatMap( subGroup => subGroup.products || [] );
	const { variantsMap: subGroupVariantsMap } = useGroupVariants( subGroupProductIds );

	const selectedCount =
		groupProductIds.filter( productId => isProductSelected( productId, variantsMap, selectedProducts ) ).length +
		subGroupProductIds.filter( productId => isProductSelected( productId, subGroupVariantsMap, selectedProducts ) ).length;

	return (
		<div ref={ wrapperRef } className="configurator-product-accordion">
			<button
				ref={ headerRef }
				type="button"
				className="configurator-product-accordion-header"
				onClick={ () => setIsOpen( ! isOpen ) }
				aria-expanded={ isOpen }
				aria-controls={ contentId }
				id={ accordionId }
			>
				<span className="configurator-product-accordion-title">
					<span className="configurator-product-accordion-name">
						{ groupName }
						{ selectedCount > 0 && (
							<span className="configurator-product-accordion-selected-count">
								<span aria-hidden="true">{ selectedCount }</span>
								<span className="screen-reader-text">
									{ sprintf(
										/* translators: %d: number of selected options in the group. */
										_n( '%d option selected', '%d options selected', selectedCount, 'at-vehicle-configurator' ),
										selectedCount,
									) }
								</span>
							</span>
						) }
					</span>
					<span className="configurator-product-accordion-count">
						{ sprintf(
							/* translators: %d: number of available product options in the group. */
							_n( '%d available option', '%d available options', totalOptions, 'at-vehicle-configurator' ),
							totalOptions,
						) }
					</span>
				</span>
				<span className="configurator-accordion-icon" aria-hidden="true">
					{ isOpen ? '−' : '+' }
				</span>
			</button>
			{ isOpen && (
				<div
					id={ contentId }
					className="configurator-product-accordion-content"
					role="region"
					aria-labelledby={ accordionId }
				>
					{ group.group_description && (
						<div
							className="configurator-product-accordion-description"
							dangerouslySetInnerHTML={ { __html: group.group_description } }
						/>
					) }
					{ group.products && group.products.length > 0 && (
						<div className={ `configurator-products-list ${ selectionType === 'radio' ? 'configurator-products-list-radio' : '' } ${ cardLayout === 'grid-2' ? 'configurator-products-list-grid-2' : '' } ${ cardLayout === 'grid-3' ? 'configurator-products-list-grid-3' : '' }` }>
							{ group.products.map( productId => {
								const isLocked = lockedProducts.includes( productId );
								const isDisabled = isRadioGroupDisabled && ! isLocked;
								const variants = variantsMap[ productId ] || [];
								const variantIds = variants.map( v => v.id );
								const activeVariantId = getActiveVariantId( variants, selectedProducts );
								const isSelected = variants.length > 0
									? activeVariantId !== null
									: selectedProducts.includes( productId );
								// The configuration stores the variant's ID, so its quantity keys off that.
								const quantityId = variants.length > 0 ? activeVariantId : productId;
								return (
									<ProductCard
										key={ productId }
										productId={ productId }
										isSelected={ isSelected }
										onToggle={ () => toggleProduct( productId ) }
										selectionType={ selectionType }
										isLocked={ isLocked }
										isDisabled={ isDisabled }
										layout={ cardLayout }
										vatMode={ vatMode }
										variants={ variants }
										activeVariantId={ activeVariantId }
										onSelectVariant={ id => selectVariant( id, variantIds ) }
										quantity={ quantities[ quantityId ] || 1 }
										onQuantityChange={ units => setProductQuantity( quantityId, units ) }
										workshopHourlyRate={ workshopHourlyRate }
										taxRate={ taxRate }
									/>
								);
							} ) }
						</div>
					) }
					{ populatedSubGroups.length > 0 && (
						<div className="configurator-sub-groups">
							{ populatedSubGroups.map( ( subGroup, index ) => (
								<NestedProductAccordion
									key={ index }
									subGroup={ subGroup }
									selectedProducts={ selectedProducts }
									setSelectedProducts={ setSelectedProducts }
									lockedProducts={ lockedProducts }
									vatMode={ vatMode }
									quantities={ quantities }
									setProductQuantity={ setProductQuantity }
									workshopHourlyRate={ workshopHourlyRate }
									taxRate={ taxRate }
								/>
							) ) }
						</div>
					) }
				</div>
			) }
		</div>
	);
};

export default ProductAccordion;
